import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AdminUserDetails } from '../models/admin.model';

@Injectable({
    providedIn: 'root'
})
export class AdminService {
    http = inject(HttpClient);

    /**
     * Fetches Keycloak user details filtered by username.
     * @param params - Object containing the `username` to search for.
     * @returns Observable emitting the matching user details.
     * @example
     * ```ts
     * getUserDetails({ username: 'jdoe' }).subscribe(users => JSON.stringify(users));
     * ```
     */
    getUserDetails(params: { username?: string }): Observable<AdminUserDetails[]>;
    /**
     * Fetches Keycloak user details filtered by phone number.
     * @param params - Object containing the `phone` to search for.
     * @returns Observable emitting the matching user details.
     * @example
     * ```ts
     * getUserDetails({ phone: '9999999999' }).subscribe(users => JSON.stringify(users));
     * ```
     */
    getUserDetails(params: { phone?: string }): Observable<AdminUserDetails[]>;
    /**
     * Fetches Keycloak user details filtered by email.
     * @param params - Object containing the `email` to search for.
     * @returns Observable emitting the matching user details.
     * @example
     * ```ts
     * getUserDetails({ email: 'jdoe@example.com' }).subscribe(users => JSON.stringify(users));
     * ```
     */
    getUserDetails(params: { email?: string }): Observable<AdminUserDetails[]>;
    getUserDetails(params: { username?: string; phone?: string; email?: string }) {
        const url = 'https://admin.lokos.in/api/keycloak/v2/graph/realms/lokos/users';
        return this.http.get<AdminUserDetails[]>(url, { params });
    }

    /**
     * Fetches the logged-in user's vertical/sub-role permissions.
     * @param subId The Keycloak subject id (the `sub` used across the app).
     * @returns A list of `Vertical:subrole` strings, e.g. `["Livelihood Farm:CRP Aajeevika"]`.
     */
    getUserSubRoles(subId: string) {
        return this.http.get<string[]>(`https://admin.lokos.in/api/keycloak/v2/graph/realms/lokos/permission/${subId}`);
    }

    /**
     * Fetches Keycloak users assigned a specific client role.
     * @param clientId - The Keycloak client ID owning the role.
     * @param roleOgName - The hyphen-separated complete name of the role to look up users for.
     * @returns Observable emitting the users assigned to the role.
     * @example
     * ```ts
     * getUsersByRole('client-uuid-format', 'block-manager-farm').subscribe(users => JSON.stringify(users));
     * ```
     */
    getUsersByRole(clientId: string, roleOgName: string) {
        const url = `https://admin.lokos.in/api/keycloak/v2/graph/realms/lokos/clients/${clientId}/roles/${roleOgName}/users`;
        return this.http.get<AdminUserDetails[]>(url);
    }

    /**
     * Fetches Keycloak users profile image.
     * @param digilockerId The keycloak user's assigned Digilocker ID (found in attributes)
     * @returns Observable emitting the user's profile image's Object URL as a string
     */
    getUserPhoto(digilockerId: string) {
        const url = 'https://admin.lokos.in/api/keycloak/v2/graph/realms/lokos/image';
        return this.http.get<string>(url, { params: { sub: digilockerId } });
    }
}
